import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import DashboardLayout from "../../components/layout/DashboardLayout";
import StatusBadge from "../../components/StatusBadge";
import { WHATSAPP_SUPPORT } from "../../components/layout/PublicFooter";
import { MessageCircle } from "lucide-react";
import { formatDateTime } from "../../lib/format";

export default function ClientDashboard() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/me/dashboard").then(({ data }) => setData(data)); }, []);

    return (
        <DashboardLayout role="client">
            <div className="mb-10" data-testid="client-dashboard">
                <div className="label-mono mb-3">/ Overview</div>
                <h1 className="font-heading font-black text-3xl lg:text-4xl tracking-tighter">Welcome to your dashboard</h1>
                <p className="text-avx-muted mt-2">Manage your AVX Pro purchases, downloads and support.</p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
                <Kpi label="Plugins purchased" value={data?.total_plugins ?? "—"} testId="kpi-plugins" />
                <Kpi label="Last purchase" value={data?.last_purchase ? formatDateTime(data.last_purchase.created_at) : "—"} testId="kpi-last-purchase" />
                <Kpi label="Last download" value={data?.last_download ? formatDateTime(data.last_download.downloaded_at) : "—"} testId="kpi-last-download" />
                <Kpi label="Open tickets" value={data?.open_tickets ?? "—"} testId="kpi-open-tickets" />
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                <div className="panel p-6">
                    <div className="flex justify-between items-center mb-4">
                        <div className="label-mono">/ Last purchase</div>
                        <Link to="/dashboard/purchases" className="text-xs font-mono uppercase tracking-[0.18em] text-avx-blue hover:underline">View all →</Link>
                    </div>
                    {data?.last_purchase ? (
                        <div className="flex items-center gap-4">
                            {data.last_purchase.product?.image_url && (
                                <img src={data.last_purchase.product.image_url} alt="" className="w-20 h-20 object-cover rounded-sm border border-avx-line" />
                            )}
                            <div className="flex-1">
                                <h3 className="font-heading font-bold text-lg">{data.last_purchase.product?.name || "—"}</h3>
                                <div className="flex items-center gap-2 mt-2">
                                    <StatusBadge status={data.last_purchase.status} />
                                    <span className="font-mono text-xs text-avx-muted">{formatDateTime(data.last_purchase.created_at)}</span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="text-avx-muted text-sm">You don't have any purchases yet.</div>
                    )}
                </div>
                <div className="panel p-6">
                    <div className="label-mono mb-4">/ Quick links</div>
                    <div className="grid grid-cols-2 gap-3">
                        <QuickLink to="/dashboard/plugins" label="My plugins" />
                        <QuickLink to="/dashboard/purchases" label="My purchases" />
                        <QuickLink to="/dashboard/downloads" label="Downloads" />
                        <QuickLink to="/dashboard/support" label="Support" />
                    </div>
                    <a
                        href={WHATSAPP_SUPPORT.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="dashboard-whatsapp-support"
                        className="mt-4 flex items-center gap-3 p-3 rounded-sm border border-[#25D366]/40 bg-[#25D366]/10 hover:bg-[#25D366]/20 transition-colors"
                    >
                        <MessageCircle className="w-5 h-5 text-[#25D366] shrink-0" />
                        <div className="flex-1">
                            <div className="text-sm font-heading font-bold text-[#25D366]">Direct WhatsApp support</div>
                            <div className="font-mono text-xs text-avx-muted">{WHATSAPP_SUPPORT.display}</div>
                        </div>
                        <span className="font-mono text-xs text-[#25D366]">→</span>
                    </a>
                </div>
            </div>
        </DashboardLayout>
    );
}

function Kpi({ label, value, testId }) {
    return (
        <div className="panel-elevated p-5" data-testid={testId}>
            <div className="label-mono mb-2">{label}</div>
            <div className="font-heading font-black text-2xl">{value}</div>
        </div>
    );
}

function QuickLink({ to, label }) {
    return (
        <Link to={to} className="border border-avx-line rounded-sm p-4 hover:border-avx-blue hover:text-avx-blue transition-colors flex items-center justify-between">
            <span className="text-sm">{label}</span>
            <span className="font-mono text-xs">→</span>
        </Link>
    );
}
